import SetupPageLayout from '../../components/setup/SetupPageLayout';
import { getAdminStep } from '../../constants/setupRoadmapSteps';
import styles from './CilogonSetup.module.css';

const ASSETS_BASE = ((process.env.PUBLIC_URL || '') + '/assets/cilogon').replace(/^\/+/, '/');
const cilogonStep = getAdminStep('cilogon');

function CilogonSetup() {
    return (
        <SetupPageLayout
            stepNumber={cilogonStep.number}
            totalSteps={cilogonStep.total}
            title="CILogon OAuth Setup"
            description="This guide walks you through configuring CILogon OAuth for institutional login on this server (girder-oauth plugin). This step is optional."
            backLink="/admin/setup"
            backLinkText="Roadmap"
            showHeaderNavigation={false}
        >
            <div className={styles.content}>
                <p>
                    This guide walks you through configuring CILogon OAuth so users can sign in with institutional
                    credentials. You will use both the Girder OAuth plugin configuration and the CILogon registration
                    page, copying values between them as described below.
                </p>

                <h2 className={styles.sectionTitle}>1. Open the OAuth plugin configuration</h2>
                <p>
                    The first step is to open the OAuth plugin configuration page for this server. From the Girder/DSA
                    administrator area you can reach it via the Plugins page (see screenshot below). For convenience,
                    you can also use this direct link:
                </p>
                <p>
                    <a href="/dsa/#plugins/oauth/config" className={styles.externalLink}>
                        Open OAuth plugin configuration (Girder admin)
                    </a>
                </p>

                <figure className={styles.figure}>
                    <div className={styles.screenshotWrapper}>
                        <img
                            src={`${ASSETS_BASE}/admin-plugins.png`}
                            alt="Admin Plugins page showing how to reach OAuth plugin"
                            className={styles.screenshot}
                        />
                    </div>
                    <figcaption>Reaching the OAuth plugin from Admin → Plugins.</figcaption>
                </figure>

                <figure className={styles.figure}>
                    <div className={styles.screenshotWrapper}>
                        <img
                            src={`${ASSETS_BASE}/oauth-cilogon.png`}
                            alt="OAuth provider configuration page with CILogon"
                            className={styles.screenshot}
                        />
                    </div>
                    <figcaption>The OAuth plugin configuration page. You will need the callback URL shown here for the next step.</figcaption>
                </figure>

                <h2 className={styles.sectionTitle}>2. Register your client with CILogon and add the callback URL</h2>
                <p>
                    On the CILogon registration page, create a new OAuth 2.0 client. You will need to provide a client
                    name, contact email, home URL, and one or more <strong>callback URLs</strong>. Copy the callback URL
                    from the OAuth plugin configuration page (screenshot above) and enter it (or add it) in the CILogon
                    form under Callback URLs.
                </p>
                <p>
                    <a
                        href="https://cilogon.org/oauth2/register"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.externalLink}
                    >
                        CILogon OAuth 2.0 Client Registration
                    </a>
                </p>

                <figure className={styles.figure}>
                    <div className={styles.screenshotWrapper}>
                        <img
                            src={`${ASSETS_BASE}/cilogon-setup.png`}
                            alt="CILogon registration form with callback URL field"
                            className={styles.screenshot}
                        />
                    </div>
                    <figcaption>Register your client on CILogon and enter the callback URL from the plugin page.</figcaption>
                </figure>

                <h2 className={styles.sectionTitle}>3. Copy client credentials back into the plugin</h2>
                <p>
                    CILogon shows your <strong>Client ID</strong> and <strong>Client Secret</strong> immediately when
                    you complete registration. Copy both values right away—they cannot be retrieved later. You can then
                    enter them into the OAuth plugin configuration page on this server (the same page you opened in
                    step 1). The plugin can be configured at any time; you do not need to wait for CILogon approval.
                </p>

                <figure className={styles.figure}>
                    <div className={styles.screenshotWrapper}>
                        <img
                            src={`${ASSETS_BASE}/cilogon-registration-success.png`}
                            alt="CILogon registration success with Client ID and Client Secret"
                            className={styles.screenshot}
                        />
                    </div>
                    <figcaption>CILogon shows your Client ID and Client Secret immediately upon registration. Copy them before leaving this page.</figcaption>
                </figure>

                <figure className={styles.figure}>
                    <div className={styles.screenshotWrapper}>
                        <img
                            src={`${ASSETS_BASE}/secret-entry.png`}
                            alt="Entering the client secret in the OAuth plugin"
                            className={styles.screenshot}
                        />
                    </div>
                    <figcaption>Enter the Client ID and Client Secret into the OAuth plugin configuration.</figcaption>
                </figure>

                <p>
                    Your client registration is reviewed by CILogon before OAuth login will work. Approval is typically
                    granted within one business day. Until then, the credentials are saved in the plugin but the CILogon
                    sign-in option will not complete successfully. Contact help@cilogon.org if you do not receive a
                    response after one business day.
                </p>
            </div>
        </SetupPageLayout>
    );
}

export default CilogonSetup;
